import { duration } from 'dayjs'
import { createSelector } from 'reselect'
import { immer } from 'zustand/middleware/immer'

import { pages } from '~/app/routes'
import { Schemas } from '~/clients/api-client'

import { StoreState } from '../store'
import { BaseFilter, createFilterSliceActions, FilterSliceActions } from './types'

export const MAX_OR_DAY_ROOM_DURATION_HOURS = 8

export const operationalPlannerFilters = [
    'location',
    'surgery-type-group',
    'block-availability',
    'available-duration-min',
    'available-duration-max',
    'practitioner',
    'available-by-count',
] as const

export const operationalPlannerMonthFilters = [
    'practitioner',
    'surgery-type-group',
    'weekday',
    'location',
    'available-duration-min',
    'available-duration-max',
    'block-availability',
    'available-by-count',
] as const

export type OperationalPlannerFilterType = (typeof operationalPlannerFilters)[number] | (typeof operationalPlannerMonthFilters)[number]

export type OperationalPlannerFilter = {
    type: OperationalPlannerFilterType
} & BaseFilter

type State = {
    filters: OperationalPlannerFilter[]
}

type Actions = {
    actions: FilterSliceActions<OperationalPlannerFilterType>
}

export type OperationalPlannerFilterSlice = {
    operationalPlannerFilters: State & Actions
}

const initialState: State = {
    filters: [],
}

// Normalizes available-duration-* values, migrating legacy ISO duration strings to fractional hours
function parseFilterHours(value: string): number {
    return value.startsWith('P') ? duration(value).asHours() : parseFloat(value)
}

const allOperationalPlannerFilterTypes = new Set<string>([...operationalPlannerFilters, ...operationalPlannerMonthFilters])

export const createOperationalPlannerFilterSlice = immer<OperationalPlannerFilterSlice>((set, get) => ({
    operationalPlannerFilters: {
        ...initialState,
        actions: {
            ...createFilterSliceActions<OperationalPlannerFilterType, OperationalPlannerFilter>('operationalPlannerFilters', set, get),
            // Normalizes available-duration-* values, migrating legacy ISO duration strings to fractional hours
            setProfile: (profile: Schemas['ViewOptionProfileOut'] | undefined) => {
                set(state => {
                    if (profile?.filter_profile) {
                        state.operationalPlannerFilters.filters = Object.entries(profile.filter_profile)
                            .filter((entry): entry is [OperationalPlannerFilterType, string[]] => allOperationalPlannerFilterTypes.has(entry[0]))
                            .flatMap(([type, filterValues]) =>
                                filterValues.map(value => {
                                    const normalizedValue =
                                        type === 'available-duration-min' || type === 'available-duration-max' ? String(parseFilterHours(value)) : value

                                    return {
                                        id: `${type}-${normalizedValue}-${normalizedValue}`,
                                        type,
                                        value: normalizedValue,
                                        label: normalizedValue,
                                    }
                                })
                            )
                    }
                })
            },
        },
    },
}))

const selectAllFilters = (state: StoreState) => state.operationalPlannerFilters.filters

// View specific selector
export const selectOperationalPlannerFiltersForView = createSelector(
    selectAllFilters,
    (state: StoreState) => state.app.activeViews.OPERATIONAL_PLANNER,
    (filters, activeView) =>
        filters.filter((filter: OperationalPlannerFilter) => {
            if (activeView === pages.OPERATIONAL_PLANNER.views.month.path) {
                return operationalPlannerMonthFilters.includes(filter.type)
            } else {
                return operationalPlannerFilters.includes(filter.type)
            }
        })
)

// Filter specific selectors
export const selectAvailableDurationMinFilter = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'available-duration-min')
)

export const selectAvailableDurationMaxFilter = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'available-duration-max')
)

export const selectAvailableDurationMinMaxFilterAsHours = createSelector(
    selectAvailableDurationMinFilter,
    selectAvailableDurationMaxFilter,
    (minFilter, maxFilter): [number, number] => [
        // Normalizes available-duration-* values, migrating legacy ISO duration strings to fractional hours
        parseFilterHours(minFilter[0]?.value ?? '0'),
        parseFilterHours(maxFilter[0]?.value ?? String(MAX_OR_DAY_ROOM_DURATION_HOURS)),
    ]
)

export const selectAvailableByCountValue = createSelector(selectAllFilters, filters =>
    filters
        .filter((filter: OperationalPlannerFilter) => filter.type === 'available-by-count')
        .some((filter: OperationalPlannerFilter) => filter.value === 'true')
)

export const selectPractitionerValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'practitioner').map((filter: OperationalPlannerFilter) => filter.value)
)

export const selectSurgeryTypeGroupValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'surgery-type-group').map((filter: OperationalPlannerFilter) => filter.value)
)

export const selectWeekdayValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'weekday').map((filter: OperationalPlannerFilter) => filter.value)
)

export const selectLocationValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'location').map((filter: OperationalPlannerFilter) => filter.value)
)

export const selectBlockAvailabilityValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: OperationalPlannerFilter) => filter.type === 'block-availability').map((filter: OperationalPlannerFilter) => filter.value)
)
